import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { AuthContext } from "../../context/AuthContext";
import { admins } from "../../Constants";
import { getMyDemos, deleteUpload, UploadedDemo } from "../../services/UserDemoService";
import Loading from "../../components/Loading";
import styles from "./UserDemos.module.css";

// "Team A vs Team B" when we have names, otherwise fall back to the original filename.
const demoTitle = (d: UploadedDemo, t: TFunction) => {
    if (d.team1Name && d.team2Name) {
        return (
            <span className={styles.matchup}>
                {d.team1Name}<span className={styles.vs}>{t("userDemos.myDemos.vs")}</span>{d.team2Name}
            </span>
        );
    }
    return <span className="text-truncate d-inline-block" style={{ maxWidth: 260 }}>{d.originalFilename ?? "—"}</span>;
};

const statusBadge = (status: string, t: TFunction) => {
    const map: Record<string, string> = {
        completed: styles.badgeSuccess,
        processing: styles.badgeWarn,
        failed: styles.badgeDanger,
    };
    return <span className={`${styles.badge} ${map[status] ?? styles.badgeWarn}`}>{t(`userDemos.status.${status}`, status)}</span>;
};

// Plain-text match label, used both for display fallback and as the sort key.
const demoMatchLabel = (d: UploadedDemo) =>
    d.team1Name && d.team2Name ? `${d.team1Name} vs ${d.team2Name}` : (d.originalFilename ?? "");

type SortKey = "match" | "map" | "status" | "uploaded";
const PAGE_SIZE = 15;

const MyDemosPage: React.FC = () => {
    const { t } = useTranslation("pages");
    const { user, isBeta } = useContext(AuthContext);
    const [demos, setDemos] = useState<UploadedDemo[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [sortKey, setSortKey] = useState<SortKey>("uploaded");
    const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
    const [page, setPage] = useState(0);
    const [searchTeam, setSearchTeam] = useState("");
    const [mapFilter, setMapFilter] = useState("");

    const isDonator = !!user && (user.donator || !!user.teamTier || isBeta || admins.includes(user.steamID));

    // Text columns default to A-Z; status/date default to newest/most-relevant first.
    const toggleSort = (key: SortKey) => {
        setSortDir(prev => (key === sortKey ? (prev === "asc" ? "desc" : "asc") : (key === "match" || key === "map" ? "asc" : "desc")));
        setSortKey(key);
        setPage(0);
    };

    // Every map name that appears among this donator's demos, for the map filter dropdown.
    const mapOptions = useMemo(() => {
        if (!demos) return [];
        return Array.from(new Set(demos.map(d => d.mapName).filter((m): m is string => !!m))).sort();
    }, [demos]);

    const filteredDemos = useMemo(() => {
        if (!demos) return [];
        const q = searchTeam.trim().toLowerCase();
        return demos.filter(d =>
            (!q || demoMatchLabel(d).toLowerCase().includes(q)) &&
            (!mapFilter || d.mapName === mapFilter)
        );
    }, [demos, searchTeam, mapFilter]);

    const sortedDemos = useMemo(() => {
        const dir = sortDir === "asc" ? 1 : -1;
        return [...filteredDemos].sort((a, b) => {
            switch (sortKey) {
                case "match": return demoMatchLabel(a).localeCompare(demoMatchLabel(b)) * dir;
                case "map": return (a.mapName ?? "").localeCompare(b.mapName ?? "") * dir;
                case "status": return a.status.localeCompare(b.status) * dir;
                case "uploaded": default:
                    return (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) * dir;
            }
        });
    }, [filteredDemos, sortKey, sortDir]);

    const totalPages = Math.max(1, Math.ceil(sortedDemos.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages - 1);
    const pageItems = sortedDemos.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

    const SortArrow = ({ column }: { column: SortKey }) =>
        sortKey !== column ? null : <span className={styles.sortArrow}>{sortDir === "asc" ? "▲" : "▼"}</span>;

    const load = async () => {
        try {
            setDemos(await getMyDemos());
        } catch {
            setError(t("userDemos.myDemos.loadError"));
        }
    };

    useEffect(() => {
        if (isDonator) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDonator]);

    const handleDelete = async (uploadId: string) => {
        if (!window.confirm(t("userDemos.myDemos.confirmDelete"))) return;
        await deleteUpload(uploadId);
        load();
    };

    if (!user) return <div className="container mt-4"><div className="alert alert-warning">{t("userDemos.myDemos.loginRequired")}</div></div>;
    if (!isDonator) return <div className="container mt-4"><div className="alert alert-info">{t("userDemos.donatorOnly.title")}</div></div>;
    if (error) return <div className="container mt-4"><div className="alert alert-danger">{error}</div></div>;
    if (!demos) return <Loading />;

    return (
        <div className="container mt-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h3 className="mb-0">{t("userDemos.myDemos.title")}</h3>
                <Link to="/upload-demo" className={styles.btnPrimary}>{t("userDemos.myDemos.uploadBtn")}</Link>
            </div>

            {demos.length > 0 && (
                <div className="d-flex flex-wrap gap-2 mb-3">
                    <input
                        type="text"
                        className={styles.input}
                        style={{ minWidth: 220, flex: "1 1 220px" }}
                        placeholder={t("userDemos.myDemos.searchPlaceholder")}
                        value={searchTeam}
                        onChange={(e) => { setSearchTeam(e.target.value); setPage(0); }}
                    />
                    <select
                        className={styles.input}
                        value={mapFilter}
                        onChange={(e) => { setMapFilter(e.target.value); setPage(0); }}
                    >
                        <option value="">{t("userDemos.myDemos.allMaps")}</option>
                        {mapOptions.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                </div>
            )}

            {demos.length === 0 ? (
                <div className={styles.banner}>{t("userDemos.myDemos.empty")}</div>
            ) : sortedDemos.length === 0 ? (
                <div className={styles.banner}>{t("userDemos.myDemos.noResults")}</div>
            ) : (
                <div className={styles.tableContainer}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th className={styles.sortableHeader} onClick={() => toggleSort("match")}>{t("userDemos.myDemos.colMatch")}<SortArrow column="match" /></th>
                                <th className={styles.sortableHeader} onClick={() => toggleSort("map")}>{t("userDemos.myDemos.colMap")}<SortArrow column="map" /></th>
                                <th className={styles.sortableHeader} onClick={() => toggleSort("status")}>{t("userDemos.myDemos.colStatus")}<SortArrow column="status" /></th>
                                <th className={styles.sortableHeader} onClick={() => toggleSort("uploaded")}>{t("userDemos.myDemos.colUploaded")}<SortArrow column="uploaded" /></th>
                                <th className="text-end">{t("userDemos.myDemos.colActions")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pageItems.map((d) => (
                                <tr key={d.uploadId} className={styles.row}>
                                    <td>{demoTitle(d, t)}</td>
                                    <td>{d.mapName ?? "—"}</td>
                                    <td>
                                        {statusBadge(d.status, t)}
                                        {d.status === "failed" && d.errorMessage && (
                                            <div className="small text-danger mt-1">{d.errorMessage}</div>
                                        )}
                                    </td>
                                    <td className={`small ${styles.muted}`}>{new Date(d.createdAt).toLocaleString()}</td>
                                    <td className="text-end">
                                        {d.status === "completed" && (
                                            <>
                                                <Link to={`/demo/${d.uploadId}`} className={`${styles.action} ${styles.actionView} me-2`}>{t("userDemos.myDemos.view")}</Link>
                                                <Link to={`/analyse?upload=${d.uploadId}`} className={`${styles.action} ${styles.actionView} me-2`}>{t("userDemos.viewer.tabAnalyse")}</Link>
                                            </>
                                        )}
                                        <button className={`${styles.action} ${styles.actionDanger}`} onClick={() => handleDelete(d.uploadId)}>{t("userDemos.myDemos.delete")}</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {totalPages > 1 && (
                <div className={styles.pagination}>
                    <button
                        className={styles.pageButton}
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                        disabled={safePage === 0}
                    >
                        ‹ {t("userDemos.myDemos.prevPage")}
                    </button>
                    <span className={styles.pageInfo}>
                        {t("userDemos.myDemos.pageInfo", { page: safePage + 1, total: totalPages })}
                    </span>
                    <button
                        className={styles.pageButton}
                        onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                        disabled={safePage >= totalPages - 1}
                    >
                        {t("userDemos.myDemos.nextPage")} ›
                    </button>
                </div>
            )}
        </div>
    );
};

export default MyDemosPage;
